import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User } from '../types';
import { apiService } from '../services/api';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (userData: RegisterData) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
}

interface RegisterData {
  email: string;
  username: string;
  password: string;
  firstName?: string;
  lastName?: string;
  timezone?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      try {
        console.log('Auth: Initializing...');
        if (apiService.isAuthenticated()) {
          const userData = apiService.getCurrentUser();
          if (userData) {
            console.log('Auth: Found cached user:', userData.username);
            setUser(userData);
          } else {
            try {
              console.log('Auth: Fetching fresh user data...');
              const { user: freshUserData } = await apiService.getProfile();
              setUser(freshUserData);
            } catch (error) {
              console.log('Auth: Could not fetch profile, clearing auth');
              apiService.logout();
              setUser(null);
            }
          }
        } else {
          console.log('Auth: No authentication found');
          setUser(null);
        }
      } catch (error) {
        console.error('Auth: Failed to initialize:', error);
        apiService.logout();
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      console.log('Auth: Login attempt for:', email);
      const response = await apiService.login({ email, password });
      if (response.success && response.user) {
        console.log('Auth: Login successful:', response.user.username);
        setUser(response.user);
      } else {
        throw new Error(response.error || 'Login failed');
      }
    } catch (error: any) {
      console.error('Auth: Login error:', error.message);
      throw new Error(error.response?.data?.error || error.message || 'Login failed');
    }
  };

  const register = async (userData: RegisterData) => {
    try {
      console.log('Auth: Register attempt for:', userData.email);
      const response = await apiService.register(userData);
      if (response.success && response.user) {
        console.log('Auth: Registration successful:', response.user.username);
        setUser(response.user);
      } else {
        throw new Error(response.error || 'Registration failed');
      }
    } catch (error: any) {
      console.error('Auth: Registration error:', error.message);
      throw new Error(error.response?.data?.error || error.message || 'Registration failed');
    }
  };

  const logout = () => {
    console.log('Auth: Logout initiated');
    apiService.logout();
    setUser(null);
    console.log('Auth: Logout completed');
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
